import { type RefObject, useCallback } from 'react'

const SELECTED_ATTRIBUTE_SELECTOR = '[data-export-attribute-key]'
const SELECTED_ITEM_MOVE_TRANSITION =
  'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)'
const SELECTED_ITEM_MOVE_FALLBACK_MS = 260

export const useSelectedItemLayoutAnimation = (
  selectedListRef: RefObject<HTMLDivElement | null>,
) => {
  const captureSelectedItemRects = useCallback(() => {
    const itemRects = new Map<string, DOMRect>()
    const selectedListElement = selectedListRef.current

    if (!selectedListElement) return itemRects

    selectedListElement
      .querySelectorAll<HTMLElement>(SELECTED_ATTRIBUTE_SELECTOR)
      .forEach((element) => {
        const attributeKey = element.dataset.exportAttributeKey
        if (!attributeKey) return

        itemRects.set(attributeKey, element.getBoundingClientRect())
      })

    return itemRects
  }, [selectedListRef])

  const animateSelectedItemsFrom = useCallback(
    (previousItemRects: Map<string, DOMRect>) => {
      window.requestAnimationFrame(() => {
        const selectedListElement = selectedListRef.current
        if (!selectedListElement) return

        selectedListElement
          .querySelectorAll<HTMLElement>(SELECTED_ATTRIBUTE_SELECTOR)
          .forEach((element) => {
            const attributeKey = element.dataset.exportAttributeKey
            if (!attributeKey) return

            const previousRect = previousItemRects.get(attributeKey)
            if (!previousRect) return

            const nextRect = element.getBoundingClientRect()
            const deltaX = previousRect.left - nextRect.left
            const deltaY = previousRect.top - nextRect.top
            if (deltaX === 0 && deltaY === 0) return

            element.style.transition = 'none'
            element.style.transform = `translate(${deltaX}px, ${deltaY}px)`

            window.requestAnimationFrame(() => {
              const cleanupAnimation = () => {
                element.style.removeProperty('transition')
                element.style.removeProperty('transform')
                element.removeEventListener('transitionend', cleanupAnimation)
              }

              element.addEventListener('transitionend', cleanupAnimation)
              element.style.transition = SELECTED_ITEM_MOVE_TRANSITION
              element.style.transform = 'translate(0, 0)'

              window.setTimeout(
                cleanupAnimation,
                SELECTED_ITEM_MOVE_FALLBACK_MS,
              )
            })
          })
      })
    },
    [selectedListRef],
  )

  return {
    animateSelectedItemsFrom,
    captureSelectedItemRects,
  }
}
